"use client";

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, RefreshCw, Search, User, Users, Shield, Lock, Unlock, Send, AlertTriangle, CheckCircle2, XCircle, ShieldOff, Award, CreditCard } from 'lucide-react';
import { getBadgeColor, BADGE_COLORS } from '@/lib/badgeColors';
import { format, formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { OrgRoleBadge } from '@/components/settings/OrgRoleBadge';
import { DisableMFADialog } from '@/components/admin/DisableMFADialog';

import { UserJobsTab } from '@/components/admin/UserJobsTab';
import { UserNotesTab } from '@/components/admin/UserNotesTab';

// Vendor Verification Badge Component
const VendorVerificationBadge = ({ 
  level, 
  isVerified 
}: { 
  level?: string | null; 
  isVerified?: boolean;
}) => {
  if (!level || level === 'none' || !isVerified) {
    return (
      <Badge className={`${BADGE_COLORS.gray} text-xs`}>
        <Shield className="h-3 w-3 mr-1" />
        Unverified
      </Badge>
    );
  }
  
  const config: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
    basic: { 
      color: BADGE_COLORS.blue, 
      icon: <Shield className="h-3 w-3 mr-1" />, 
      label: 'Basic' 
    },
    enhanced: { 
      color: BADGE_COLORS.purple, 
      icon: <Shield className="h-3 w-3 mr-1" />, 
      label: 'Enhanced' 
    },
    premium: { 
      color: BADGE_COLORS.amber, 
      icon: <Award className="h-3 w-3 mr-1" />, 
      label: 'Premium' 
    },
  };
  
  const { color, icon, label } = config[level] || config.basic;
  
  return (
    <Badge className={`${color} text-xs`}>
      {icon}
      {label}
    </Badge>
  );
};

// Vendor Stripe Status Badge Component
const VendorStripeBadge = ({ 
  connected, 
  payoutsEnabled 
}: { 
  connected?: boolean; 
  payoutsEnabled?: boolean;
}) => {
  if (!connected && !payoutsEnabled) {
    return (
      <Badge className={`${BADGE_COLORS.gray} text-xs`}>
        <CreditCard className="h-3 w-3 mr-1" />
        No Stripe
      </Badge>
    );
  }
  
  if (!connected) {
    return (
      <Badge className={`${BADGE_COLORS.orange} text-xs`}>
        <CreditCard className="h-3 w-3 mr-1" />
        Stripe Pending
      </Badge>
    );
  }
  
  return (
    <Badge className={`${BADGE_COLORS.green} text-xs`}>
      <CreditCard className="h-3 w-3 mr-1" />
      Stripe ✓
    </Badge>
  );
};

interface EnrichedUser {
  id: string; // Alias for DataTable compatibility
  user_id: string;
  email: string;
  email_verified: boolean;
  platform_role: string;
  display_name: string;
  first_name?: string;
  last_name?: string;
  business_name?: string;
  organization_id?: string;
  organization_name?: string;
  organization_status?: string;
  org_role?: string;
  created_at: string;
  last_sign_in_at?: string;
  banned_until?: string;
  is_suspended?: boolean;
  profile_image_url?: string;
  mfa_enabled?: boolean;
  admin_notes?: string;
  // Vendor-specific fields
  verification_level?: string | null;
  is_verified?: boolean;
  stripe_connected?: boolean;
  stripe_payouts_enabled?: boolean;
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<EnrichedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<EnrichedUser | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showMFADialog, setShowMFADialog] = useState(false);
  const [mfaTargetUser, setMFATargetUser] = useState<{ id: string; email: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [emailFilter, setEmailFilter] = useState<string>('all');
  const [orgStatusFilter, setOrgStatusFilter] = useState<string>('all');
  const { toast } = useToast();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.rpc('get_users_admin_enriched' as any);

      if (error) throw error;

      // Map user_id to id for DataTable compatibility
      const usersWithId = (data || []).map((user: any) => ({
        ...user,
        id: user.user_id
      }));
      
      setUsers(usersWithId);

      // Keep the modal in sync with refreshed data (e.g. admin_notes)
      if (selectedUser) {
        const refreshedSelected = usersWithId.find(
          (u: EnrichedUser) => u.user_id === selectedUser.user_id
        );
        if (refreshedSelected) setSelectedUser(refreshedSelected);
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      toast({
        title: "Error",
        description: "Failed to fetch users: " + err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const { data, error } = await supabase.rpc('admin_update_user_role' as any, {
        p_user_id: userId,
        p_new_role: newRole
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `User role updated from ${data.old_role} to ${data.new_role}`,
      });
      
      await fetchUsers();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update user role: " + err.message,
        variant: "destructive",
      });
    }
  };

  const sendPasswordReset = async (userId: string) => {
    if (!confirm('Send password reset email to this user?')) return;
    
    try {
      const { error } = await supabase.rpc('admin_send_password_reset' as any, {
        p_user_id: userId
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Password reset email queued",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to send password reset: " + err.message,
        variant: "destructive",
      });
    }
  };

  const banUser = async (userId: string, days: number) => {
    if (!confirm(`Ban this user for ${days} days?`)) return;
    
    try {
      const bannedUntil = new Date();
      bannedUntil.setDate(bannedUntil.getDate() + days);
      
      const { error } = await supabase.rpc('admin_ban_user' as any, {
        p_user_id: userId,
        p_banned_until: bannedUntil.toISOString()
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `User banned until ${bannedUntil.toLocaleDateString()}`,
      });
      
      await fetchUsers();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to ban user: " + err.message,
        variant: "destructive",
      });
    }
  };

  const unbanUser = async (userId: string) => {
    if (!confirm('Remove ban from this user?')) return;
    
    try {
      const { error } = await supabase.rpc('admin_unban_user' as any, {
        p_user_id: userId
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User unbanned",
      });
      
      await fetchUsers();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to unban user: " + err.message,
        variant: "destructive",
      });
    }
  };

  const suspendUser = async (userId: string) => {
    if (!confirm('Suspend this user account? They will not be able to log in.')) return;
    
    try {
      const { error } = await supabase.rpc('admin_suspend_user' as any, {
        p_user_id: userId
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User account suspended",
      });
      
      await fetchUsers();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to suspend user: " + err.message,
        variant: "destructive",
      });
    }
  };

  const unsuspendUser = async (userId: string) => {
    if (!confirm('Unsuspend this user account?')) return;
    
    try {
      const { error } = await supabase.rpc('admin_unsuspend_user' as any, {
        p_user_id: userId
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User account unsuspended",
      });
      
      await fetchUsers();
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to unsuspend user: " + err.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getRoleBadgeVariant = (role?: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'client': return 'default';
      case 'vendor': return 'secondary';
      default: return 'outline';
    }
  };

  const getOrgStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'suspended': return 'destructive';
      default: return 'outline';
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.platform_role === roleFilter;
    const matchesEmail = emailFilter === 'all' || 
      (emailFilter === 'verified' && user.email_verified) ||
      (emailFilter === 'unverified' && !user.email_verified);
    const matchesOrgStatus = orgStatusFilter === 'all' || user.organization_status === orgStatusFilter;
    
    return matchesSearch && matchesRole && matchesEmail && matchesOrgStatus;
  });

  const userColumns = [
    {
      key: 'display_name' as keyof EnrichedUser,
      label: 'User',
      sortable: true,
      render: (value: string, user: EnrichedUser) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={user.profile_image_url} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials(value)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{value}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              {user.email}
              {user.email_verified ? (
                <CheckCircle2 className="h-3 w-3 text-green-500" />
              ) : (
                <XCircle className="h-3 w-3 text-orange-500" />
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'platform_role' as keyof EnrichedUser,
      label: 'Platform Role',
      sortable: true,
      render: (value: string, user: EnrichedUser) => (
        <div className="space-y-1">
          <Badge variant={getRoleBadgeVariant(value)} className="capitalize">
            {value || 'No role'}
          </Badge>
          {value === 'vendor' && (
            <div className="flex flex-wrap gap-1">
              <VendorVerificationBadge 
                level={user.verification_level} 
                isVerified={user.is_verified} 
              />
              <VendorStripeBadge 
                connected={user.stripe_connected} 
                payoutsEnabled={user.stripe_payouts_enabled} 
              />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'organization_name' as keyof EnrichedUser,
      label: 'Organization',
      sortable: true,
      render: (value: string, user: EnrichedUser) => (
        <div className="space-y-1">
          <div className="font-medium">{value || 'None'}</div>
          {user.org_role && (
            <OrgRoleBadge role={user.org_role} />
          )}
          {user.organization_status && (
            <Badge variant={getOrgStatusBadgeVariant(user.organization_status)} className="capitalize text-xs">
              {user.organization_status}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'created_at' as keyof EnrichedUser,
      label: 'Created',
      sortable: true,
      render: (value: string) => format(new Date(value), 'MM/dd/yyyy'),
    },
    {
      key: 'last_sign_in_at' as keyof EnrichedUser,
      label: 'Last Sign In',
      sortable: true,
      render: (value?: string) => value ? format(new Date(value), 'MM/dd/yyyy') : 'Never',
    },
    {
      key: 'banned_until' as keyof EnrichedUser,
      label: 'Status',
      render: (value?: string, user?: EnrichedUser) => {
        if (user?.is_suspended) return <Badge variant="destructive">Suspended</Badge>;
        if (!value) return <Badge variant="outline">Active</Badge>;
        const bannedDate = new Date(value);
        if (bannedDate <= new Date()) return <Badge variant="outline">Active</Badge>;
        return <Badge variant="destructive">Banned until {bannedDate.toLocaleDateString()}</Badge>;
      },
    },
  ];

  const renderActions = (user: EnrichedUser) => {
    const isBanned = user.banned_until && new Date(user.banned_until) > new Date();
    const isSuspended = user.is_suspended;
    const hasMFA = user.mfa_enabled; // Assuming this field exists from get_users_admin_enriched
    
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedUser(user);
            setShowDetailDialog(true);
          }}
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Select
          value={user.platform_role || ''}
          onValueChange={(value) => updateUserRole(user.user_id, value)}
        >
          <SelectTrigger className="w-[110px] h-9">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="client">Client</SelectItem>
            <SelectItem value="vendor">Vendor</SelectItem>
          </SelectContent>
        </Select>
        {hasMFA && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setMFATargetUser({ id: user.user_id, email: user.email });
              setShowMFADialog(true);
            }}
            className="text-orange-600"
            title="Disable 2FA for locked-out user"
          >
            <ShieldOff className="h-4 w-4" />
          </Button>
        )}
        {isSuspended ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => unsuspendUser(user.user_id)}
            className="text-green-600"
            title="Unsuspend account"
          >
            <CheckCircle2 className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => suspendUser(user.user_id)}
            className="text-red-600"
            title="Suspend account"
          >
            <XCircle className="h-4 w-4" />
          </Button>
        )}
        {isBanned ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => unbanUser(user.user_id)}
            className="text-green-600"
            title="Remove temporary ban"
          >
            <Unlock className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => banUser(user.user_id, 7)}
            className="text-orange-600"
            title="Ban for 7 days"
          >
            <Lock className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  if (loading && users.length === 0) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const stats = {
    total: users.length,
    admins: users.filter(u => u.platform_role === 'admin').length,
    clients: users.filter(u => u.platform_role === 'client').length,
    vendors: users.filter(u => u.platform_role === 'vendor').length,
    unverified: users.filter(u => !u.email_verified).length,
    banned: users.filter(u => u.banned_until && new Date(u.banned_until) > new Date()).length,
    suspended: users.filter(u => u.organization_status === 'suspended').length,
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage user accounts, roles, and permissions</p>
        </div>
        <Button onClick={fetchUsers} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Admins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.admins}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Clients / Vendors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.clients} / {stats.vendors}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="text-sm">
                <span className="text-orange-600">{stats.unverified}</span> unverified
              </div>
              <div className="text-sm">
                <span className="text-red-600">{stats.banned}</span> banned
              </div>
              <div className="text-sm">
                <span className="text-destructive">{stats.suspended}</span> suspended orgs
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                </SelectContent>
              </Select>
              <Select value={emailFilter} onValueChange={setEmailFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="Email Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Emails</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="unverified">Unverified</SelectItem>
                </SelectContent>
              </Select>
              <Select value={orgStatusFilter} onValueChange={setOrgStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="Org Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orgs</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>Complete list of platform users</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={userColumns}
            data={filteredUsers}
            actions={renderActions}
            searchable={false}
          />
        </CardContent>
      </Card>

      {selectedUser && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={selectedUser.profile_image_url} />
                  <AvatarFallback>{getInitials(selectedUser.display_name)}</AvatarFallback>
                </Avatar>
                {selectedUser.display_name}
              </DialogTitle>
              <DialogDescription>{selectedUser.email}</DialogDescription>
            </DialogHeader>
            <UserDetailTabs 
              user={selectedUser} 
              onUpdate={fetchUsers}
              onSendPasswordReset={sendPasswordReset}
              onBanUser={banUser}
              onUnbanUser={unbanUser}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {mfaTargetUser && (
        <DisableMFADialog
          open={showMFADialog}
          onOpenChange={setShowMFADialog}
          userId={mfaTargetUser.id}
          userEmail={mfaTargetUser.email}
          onSuccess={() => {
            setShowMFADialog(false);
            setMFATargetUser(null);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
}

function UserDetailTabs({ 
  user, 
  onUpdate,
  onSendPasswordReset,
  onBanUser,
  onUnbanUser
}: { 
  user: EnrichedUser; 
  onUpdate: () => void;
  onSendPasswordReset: (userId: string) => void;
  onBanUser: (userId: string, days: number) => void;
  onUnbanUser: (userId: string) => void;
}) {
  const isBanned = user.banned_until && new Date(user.banned_until) > new Date();

  const getRoleBadgeVariant = (role?: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'client': return 'default';
      case 'vendor': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <Tabs defaultValue="profile" className="w-full">
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="profile">Profile</TabsTrigger>
        <TabsTrigger value="organization">Organization</TabsTrigger>
        <TabsTrigger value="jobs">Jobs</TabsTrigger>
        <TabsTrigger value="notes">Notes</TabsTrigger>
        <TabsTrigger value="security">Security</TabsTrigger>
      </TabsList>
      
      <TabsContent value="profile" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-medium text-muted-foreground">User ID</div>
                <code className="text-xs">{user.user_id}</code>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Email</div>
                <div className="flex items-center gap-2">
                  <span>{user.email}</span>
                  {user.email_verified ? (
                    <Badge variant="default" className="text-xs">Verified</Badge>
                  ) : (
                    <Badge variant="destructive" className="text-xs">Unverified</Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Display Name</div>
                <div>{user.display_name}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Platform Role</div>
                <Badge variant={getRoleBadgeVariant(user.platform_role)} className="capitalize">
                  {user.platform_role || 'No role'}
                </Badge>
              </div>
            </div>

            {user.first_name && user.last_name && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">First Name</div>
                  <div>{user.first_name}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Last Name</div>
                  <div>{user.last_name}</div>
                </div>
              </div>
            )}

            {user.business_name && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Business Name</div>
                <div>{user.business_name}</div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Created</div>
                <div>{formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(user.created_at).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Last Sign In</div>
                <div>
                  {user.last_sign_in_at 
                    ? formatDistanceToNow(new Date(user.last_sign_in_at), { addSuffix: true })
                    : 'Never'}
                </div>
                {user.last_sign_in_at && (
                  <div className="text-xs text-muted-foreground">
                    {new Date(user.last_sign_in_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="organization">
        <Card>
          <CardHeader>
            <CardTitle>Organization Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {user.organization_id ? (
              <>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Organization Name</div>
                  <div className="font-medium">{user.organization_name}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Organization Role</div>
                    {user.org_role && <OrgRoleBadge role={user.org_role} />}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Organization Status</div>
                    {user.organization_status && (
                      <Badge 
                        variant={user.organization_status === 'active' ? 'default' : 'destructive'}
                        className="capitalize"
                      >
                        {user.organization_status}
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Organization ID</div>
                  <code className="text-xs">{user.organization_id}</code>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                User is not a member of any organization
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="jobs">
        <UserJobsTab userId={user.user_id} />
      </TabsContent>
      
      <TabsContent value="notes">
        <UserNotesTab 
          userId={user.user_id} 
          initialNotes={user.admin_notes}
          onUpdate={onUpdate}
        />
      </TabsContent>
      
      <TabsContent value="security">
        <Card>
          <CardHeader>
            <CardTitle>Security Settings</CardTitle>
            <CardDescription>Account security and access controls</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Account Status</div>
                <div className="text-sm text-muted-foreground">
                  {isBanned ? (
                    <span className="text-red-600">
                      Banned until {new Date(user.banned_until!).toLocaleDateString()}
                    </span>
                  ) : (
                    'Active'
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                {isBanned ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onUnbanUser(user.user_id)}
                  >
                    <Unlock className="h-4 w-4 mr-1" />
                    Unban
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onBanUser(user.user_id, 7)}
                    >
                      <Lock className="h-4 w-4 mr-1" />
                      Ban 7 Days
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onBanUser(user.user_id, 30)}
                    >
                      <Lock className="h-4 w-4 mr-1" />
                      Ban 30 Days
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Password Reset</div>
                <div className="text-sm text-muted-foreground">
                  Send password reset email to user
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSendPasswordReset(user.user_id)}
              >
                <Send className="h-4 w-4 mr-1" />
                Send Reset Link
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Email Verification</div>
                <div className="text-sm text-muted-foreground">
                  {user.email_verified ? (
                    <span className="text-green-600">Email verified</span>
                  ) : (
                    <span className="text-orange-600">Email not verified</span>
                  )}
                </div>
              </div>
              {!user.email_verified && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Resend verification email
                    alert('Resend verification not yet implemented');
                  }}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Resend Verification
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
